import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  CALCULATOR_RANGES,
  CALCULATOR_STORAGE_KEY,
} from "commons/earnings.constants";
import { saveToSessionStorage } from "helpers/sessionStorage.helper";

/**
 * Clamp a URL/stored value into a slider's range, falling back to its default.
 */
function readValue(raw, { min, max, default: fallback }) {
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

/**
 * Calculator inputs, mirrored into the URL and into session storage.
 *
 * The URL is what makes an estimate shareable: a partner can send their own
 * numbers to a co-organiser, and sales can send a community a link that already
 * shows what they'd earn. Session storage is what carries those numbers forward
 * into signup and the application-status waiting room without a backend.
 */
function useCalculatorState() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Read the live location inside the effect without depending on it — taking
  // it as a dependency would re-run the effect from its own write and loop.
  const locationRef = useRef(location);
  locationRef.current = location;

  const [members, setMembers] = useState(() =>
    readValue(searchParams.get("members"), CALCULATOR_RANGES.members),
  );
  const [bookingRate, setBookingRate] = useState(() =>
    readValue(searchParams.get("rate"), CALCULATOR_RANGES.bookingRate),
  );

  useEffect(() => {
    const current = locationRef.current;
    const next = new URLSearchParams(current.search);
    next.set("members", String(members));
    next.set("rate", String(bookingRate));

    // Navigate rather than setSearchParams: the latter drops the hash, which
    // would both break `/calculator#estimate` deep links and strip any
    // campaign `utm_` fragment a partner arrived with.
    navigate(
      { search: `?${next.toString()}`, hash: current.hash },
      { replace: true },
    );

    saveToSessionStorage(CALCULATOR_STORAGE_KEY, "inputs", {
      members,
      bookingRate,
    });
  }, [members, bookingRate, navigate]);

  return { members, setMembers, bookingRate, setBookingRate };
}

export default useCalculatorState;
